import React, { useState, useEffect } from 'react';
import {
    MessageSquare, Send, CheckCircle, Clock,
    AlertCircle, RefreshCw, Hash, Cpu,
    ChevronRight, Loader2, Bug, Lightbulb, Activity
} from "lucide-react";
import { feedbackAPI } from '../../services/api';
import moment from 'moment';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../components/ui/Button';
import SEO from '../../components/shared/SEO';

const UserSupportPage = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [view, setView] = useState('all'); // 'all' or 'form' for mobile toggle

    const [formData, setFormData] = useState({
        type: 'bug',
        subject: '',
        message: ''
    });

    useEffect(() => {
        loadMyHistory();
    }, []);

    const loadMyHistory = async () => {
        setLoading(true);
        try {
            const data = await feedbackAPI.getMyTickets();
            setTickets(data);
        } catch (err) {
            console.error("Failed to load tickets");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await feedbackAPI.submitReport(formData);
            setFormData({ type: 'bug', subject: '', message: '' });
            await loadMyHistory();
            setView('all'); // Switch back to history view on mobile
        } catch (err) {
            // Error handled by interceptor
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <SEO title="Diagnostics | Engineering Support" description="Submit system bug reports or feature proposals directly to the EnvDock engineering team." noIndex={true} />

            <div className="max-w-6xl mx-auto space-y-8 pb-24">

                {/* --- Dashboard Header --- */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 ">
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            
                            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tighter ">
                                Support Ops
                            </h1>
                        </div>
                        <p className="text-gray-500 font-medium text-sm md:text-base">
                            System diagnostics and active ticket surveillance.
                        </p>
                    </div>

                    <button
                        onClick={loadMyHistory}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white/[0.02] border border-white/5 rounded-xl text-[10px] font-black text-orange-500 uppercase tracking-widest hover:bg-orange-500/10 transition-all w-full md:w-auto"
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Sync Records
                    </button>
                </div>

                <div className="grid lg:grid-cols-12 gap-10">

                    {/* --- LEFT: NEW TICKET FORM (Sticky on Desktop) --- */}
                    <div className={`lg:col-span-5 space-y-6 ${view === 'all' ? 'hidden lg:block' : 'block'}`}>
                        <div className="sticky top-24 bg-[#0a0a0a] border border-white/5 rounded-[2rem] p-6 md:p-8 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 via-red-500 to-orange-400" />

                            <h2 className="text-sm font-black text-white uppercase tracking-[0.2em] mb-8 flex items-center gap-2">
                                <MessageSquare size={16} className="text-orange-500" /> Initialize Ticket
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <TypeSelector
                                        active={formData.type === 'bug'}
                                        onClick={() => setFormData({ ...formData, type: 'bug' })}
                                        icon={Bug}
                                        label="Bug"
                                        activeClass="border-red-500 text-red-500 bg-red-500/5"
                                    />
                                    <TypeSelector
                                        active={formData.type === 'feature'}
                                        onClick={() => setFormData({ ...formData, type: 'feature' })}
                                        icon={Lightbulb}
                                        label="Request"
                                        activeClass="border-blue-500 text-blue-500 bg-blue-500/5"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-700 uppercase tracking-widest ml-1">Subject</label>
                                    <input
                                        type="text" required
                                        className="w-full bg-black/40 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-orange-500 outline-none transition-all"
                                        placeholder="Component failure details..."
                                        value={formData.subject}
                                        onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-700 uppercase tracking-widest ml-1">Description</label>
                                    <textarea
                                        required rows={5}
                                        className="w-full bg-black/40 border border-white/10 rounded-2xl p-4 text-sm text-white font-mono focus:border-orange-500 outline-none resize-none transition-all"
                                        placeholder="Detailed diagnostics..."
                                        value={formData.message}
                                        onChange={e => setFormData({ ...formData, message: e.target.value })}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={isSubmitting}
                                    className="w-full h-14"
                                >
                                    {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <><Send size={16} /> Dispatch Ticket</>}
                                </Button>

                                {view === 'form' && (
                                    <button
                                        type="button"
                                        onClick={() => setView('all')}
                                        className="w-full text-[10px] font-black text-gray-600 uppercase tracking-widest pt-2"
                                    >
                                        Cancel Transmission
                                    </button>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* --- RIGHT: TICKET HISTORY --- */}
                    <div className={`lg:col-span-7 space-y-6 ${view === 'form' ? 'hidden lg:block' : 'block'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-sm font-black text-white uppercase tracking-[0.2em] flex items-center gap-2">
                                <Clock size={16} className="text-orange-500" /> Transmission History
                            </h2>
                            <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest">{tickets.length} Total</span>
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-24 gap-4">
                                <Loader2 size={32} className="animate-spin text-orange-500/20" />
                                <p className="text-[10px] font-black text-gray-700 uppercase tracking-widest">Accessing Ledger...</p>
                            </div>
                        ) : tickets.length === 0 ? (
                            <div className="bg-[#0a0a0a] border border-dashed border-white/5 rounded-[2rem] p-16 text-center">
                                <ShieldCheck size={48} className="mx-auto text-gray-800 mb-6" />
                                <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest">No active incidents detected.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {tickets.map((ticket) => (
                                    <TicketCard key={ticket._id} ticket={ticket} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* MOBILE FLOATING ACTION BUTTON */}
                {view === 'all' && (
                    <div className="fixed bottom-8 right-6 z-50 lg:hidden">
                        <button
                            onClick={() => setView('form')}
                            className="w-16 h-16 bg-orange-500 text-white rounded-full shadow-2xl shadow-orange-500/40 flex items-center justify-center active:scale-90 transition-transform"
                        >
                            <MessageSquare size={24} />
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

// --- Sub-Components ---

const TypeSelector = ({ active, onClick, icon: Icon, label, activeClass }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex items-center justify-center gap-3 p-4 rounded-2xl border transition-all duration-300 font-black uppercase text-[10px] tracking-widest
            ${active ? activeClass : 'bg-white/[0.02] border-white/5 text-gray-700 hover:text-gray-400'}
        `}
    >
        <Icon size={14} /> {label}
    </button>
);

const TicketCard = ({ ticket }) => {
    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'resolved': return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
            case 'acknowledged': return "bg-orange-500/10 text-orange-500 border-orange-500/20";
            case 'in-progress': return "bg-blue-500/10 text-blue-500 border-blue-500/20";
            default: return "bg-white/5 text-gray-500 border-white/10";
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0a0a0a] border border-white/5 rounded-[1.5rem] p-5 md:p-6 hover:border-white/10 transition-all group"
        >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="text-[9px] font-black text-gray-600 bg-white/5 px-2 py-1 rounded-lg flex items-center gap-1.5 uppercase tracking-tighter">
                        <Hash size={10} /> {ticket._id.slice(-6).toUpperCase()}
                    </div>
                    <div className={`text-[9px] font-black uppercase px-2 py-1 rounded-lg border tracking-widest ${getStatusStyle(ticket.status)}`}>
                        {ticket.status || 'Pending'}
                    </div>
                </div>
                <span className="text-[10px] text-gray-700 font-black uppercase tracking-widest flex items-center gap-2">
                    <Clock size={12} /> {moment(ticket.createdAt).fromNow()}
                </span>
            </div>

            <div className="space-y-2">
                <h3 className="font-black text-white text-base md:text-lg tracking-tight group-hover:text-orange-500 transition-colors">
                    {ticket.subject}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 line-clamp-2 leading-relaxed font-medium">
                    {ticket.message}
                </p>
            </div>

           
        </motion.div>
    );
};

const ShieldCheck = ({ size, className }) => (
    <div className={className}>
        <Cpu size={size} />
    </div>
);

export default UserSupportPage;